import * as Sentry from "@sentry/angular";
import { Dayjs } from "dayjs";
import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, EMPTY, interval, Observable } from "rxjs";
import { distinctUntilChanged, first, map, shareReplay, startWith, switchMap, tap, } from "rxjs/operators";
import { LorenSession } from "./constants/loren-user-session.constants";
import { LOREN_HEADERS } from "../../constants/loren-headers.constants";
import { environment } from "../../../../environments/environment";
import { LorenFeature } from "../../enums/loren-feature.enum";
import { LorenLocalStorageSession } from "../../interfaces/loren-local-storage-session.interface";
import { AuthenticationToken } from "../../interfaces/authentication-token.interface";
import { JwtPayload } from "../../interfaces/jwt-payload.interface";
import { LorenUserInfo } from "../../interfaces/loren-user-info.interface";
import { LorenPrivilegeEntityType } from "../../../features/loren-roles/interfaces/loren-role.interface";
import { GenericItems } from "../../interfaces/generic-items.interface";

import { LorenAuthenticationEndpointsService } from "./loren-authentication-endpoints.service";
import { LorenUsersEndpointsService } from "../loren-users-endpoints-service/loren-users-endpoints.service";
import { LorenDateUtilsService } from "../loren-date-utils-service/loren-date-utils.service";
import { LorenUtilsService } from "../loren-utils-service/loren-utils.service";

@Injectable({
  providedIn: "root",
})
export class LorenAuthenticationService {
  private http = inject(HttpClient);
  private lorenAuthenticationEndpointsService = inject(LorenAuthenticationEndpointsService);
  private endpoints = inject(LorenUsersEndpointsService);
  private userInfo: BehaviorSubject<LorenUserInfo> =
    new BehaviorSubject<LorenUserInfo>(null);

  // New observables
  public readonly userInfo$ = this.userInfo.asObservable();
  public readonly userId$ = this.userInfo$.pipe(
      map(x => x?.id ?? null),
      distinctUntilChanged(),
  );



  private isUserAuthenticated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private cachedSession: LorenLocalStorageSession;
  private featuresLastCalled: Date;
  private featuresLastCachedData: Observable<LorenFeature[]>;
  private featuresCacheExpiration = 10 * 60 * 1000; // 10 minutes
  private currentUserLatestPrivileges: LorenPrivilegeEntityType[];
  private privilegesInterval = interval(10 * 60 * 1000).pipe(
    startWith(0),
    switchMap(() =>
      this.http.get<GenericItems<LorenPrivilegeEntityType>>(
        this.endpoints.currentUserPrivileges()
      )
    ),
    tap({
      next: (x) => (this.currentUserLatestPrivileges = x.items),
      error: () => (this.currentUserLatestPrivileges = []),
    })
  );

  refreshPrivileges$ = this.isUserAuthenticated.pipe(
    switchMap((authenticated) =>
      authenticated ? this.privilegesInterval : EMPTY
    )
  );

  constructor() {
    this.userInfo.subscribe(user => {
      if(user) {
        Sentry.setUser({
          id: user.id,
          username: user.username,
          email: user.email,
          companyID: user.companyId,
        })
      } else {
        Sentry.setUser(null)
      }
    })
  }

  get userId(): string {
    return this.userInfo.value.id;
  }

  getUserInfo() {
    return this.userInfo.value;
  }

  getUserInfoSubscription() {
    return this.userInfo.asObservable();
  }

  updateUserInfo() {
    // TODO refactor this
    this.http
      .get(this.endpoints.userInfo())
      .pipe(first())
      .subscribe((res) => {
        const userInfo = res as LorenUserInfo;
        const session: LorenLocalStorageSession = this.getSession();
        session.userInfo = userInfo;
        this.setSession(session);
        this.cachedSession = session;
        this.userInfo.next(userInfo);
      });
  }

  authenticate(username: string, password: string) {
    const token = environment.loginToken;
    return this.http
      .post(this.lorenAuthenticationEndpointsService.login(), {
        username,
        password,
        token,
      })
      .pipe(
        switchMap((authRes) =>
          this.establishSession(authRes as AuthenticationToken)
        )
      );
  }

  // Swaps the current session for an impersonation token minted by the backend.
  // Reuses the exact stateless JWT session mechanism as a normal login.
  impersonate(url: string): Observable<void> {
    return this.http
      .post(url, {})
      .pipe(
        tap(() => this.stashOriginalSession()),
        switchMap((authRes) =>
          this.establishSession(authRes as AuthenticationToken)
        )
      );
  }

  // Snapshots the current (admin) session so impersonation can be reverted.
  private stashOriginalSession(): void {
    const raw = window.localStorage.getItem(LorenSession.sessionKey);
    // Skip if nothing to stash, or if one is already stashed (nested
    // impersonation — keep the real admin session, not an impersonated one).
    if (!raw || window.localStorage.getItem(LorenSession.impersonatorSessionKey)) {
      return;
    }
    window.localStorage.setItem(LorenSession.impersonatorSessionKey, raw);
  }

  // Restores the admin session stashed before impersonation. Returns false
  // when there's nothing to restore (caller should fall back to logout).
  stopImpersonation(): boolean {
    const raw = window.localStorage.getItem(LorenSession.impersonatorSessionKey);
    if (!raw) {
      return false;
    }
    window.localStorage.setItem(LorenSession.sessionKey, raw);
    window.localStorage.removeItem(LorenSession.impersonatorSessionKey);
    this.cachedSession = null;
    this.userInfo.next((JSON.parse(raw) as LorenLocalStorageSession).userInfo);
    return true;
  }

  // Returns actor/target info when the active session is an impersonation
  // token, otherwise null. Used by the impersonation banner.
  getImpersonation(): {
    impersonatorUsername: string;
    username: string;
    expiresAt: Dayjs;
  } | null {
    const session = this.getSession();
    if (!session) {
      return null;
    }

    const jwtPayload: JwtPayload = JSON.parse(
      atob(session.token.split(".")[1])
    );
    if (!jwtPayload.impersonation) {
      return null;
    }

    return {
      impersonatorUsername: jwtPayload.impersonatorUsername,
      username: jwtPayload.username,
      expiresAt: session.expiresAt,
    };
  }

  private establishSession(authToken: AuthenticationToken): Observable<void> {
    const jwtPayload: JwtPayload = JSON.parse(
      atob(authToken.accessToken.split(".")[1])
    );
    return this.http
      .get(this.endpoints.userInfo(), {
        headers: new HttpHeaders().set(
          LOREN_HEADERS.AUTHORIZATION,
          `${authToken.tokenType} ${authToken.accessToken}`
        ),
      })
      .pipe(
        map((userInfoRes) => {
          const userInfo = userInfoRes as LorenUserInfo;
          const localStorageSession: LorenLocalStorageSession = {
            expiresAt: LorenDateUtilsService.getMomentFromUnix(jwtPayload.exp),
            tokenType: authToken.tokenType,
            token: authToken.accessToken,
            userInfo,
            ver: 2,
          };

          if (!LorenUtilsService.getLastSelectedLanguage()) {
            LorenUtilsService.updateLastSelectedLanguage(userInfo.locale);
          }

          this.setSession(localStorageSession);

          this.userInfo.next(userInfo);
        })
      );
  }

  isAuthenticated(): boolean {
    const session: LorenLocalStorageSession = this.getSession();

    if (!session) {
      this.isUserAuthenticated.next(false);
      return false;
    }

    const expired =
      session.ver !== 2 ||
      LorenDateUtilsService.getMomentNow().isAfter(session.expiresAt);

    if (!expired && !this.getUserInfo()) {
      this.userInfo.next(session.userInfo);
    }

    if (expired) {
      this.logout();
    }
    this.isUserAuthenticated.next(!expired);
    return !expired;
  }

  getRoles(): string[] {
    const session: LorenLocalStorageSession = this.getSession();

    if (!session) {
      return [];
    } else {
      const jwtPayload: JwtPayload = JSON.parse(
        atob(session.token.split(".")[1])
      );
      return jwtPayload.roles;
    }
  }

  getUserRoles(): string[] {
    const session: LorenLocalStorageSession = this.getSession();

    if (!session) {
      return [];
    } else {
      const jwtPayload: JwtPayload = JSON.parse(
          atob(session.token.split(".")[1])
      );
      return jwtPayload.userRoles;
    }
  }

  getPrivileges(): string[] {
    const session: LorenLocalStorageSession = this.getSession();
    if (!session) {
      return [];
    }

    if (this.currentUserLatestPrivileges?.length > 0) {
      return this.currentUserLatestPrivileges;
    }

    const jwtPayload: JwtPayload = JSON.parse(
      atob(session.token.split(".")[1])
    );
    return jwtPayload.privileges;
  }

  logout(): void {
    if (this.getUserInfo()) {
      // Notify the backend while the session is still present so the interceptor
      // can attach the auth header. Log out locally regardless of the outcome.
      this.http
        .post(`${environment.apiServerUrl}/logout`, {})
        .subscribe();
    }

    this.cachedSession = null;
    window.localStorage.removeItem(LorenSession.sessionKey);
    window.localStorage.removeItem(LorenSession.impersonatorSessionKey);
    this.userInfo.next(null);
  }

  getSession(): LorenLocalStorageSession {
    if (this.cachedSession) {
      return this.cachedSession;
    }

    let session = null;
    try {
      const item = window.localStorage.getItem(LorenSession.sessionKey);
      session = JSON.parse(item);
      this.cachedSession = session;
    } catch {
      // ignore error
    }

    return session;
  }

  getUserFeatures(): Observable<LorenFeature[]> {
    const now = new Date();
    const isCacheExpired =
      now.getTime() - this.featuresLastCalled?.getTime() >=
      this.featuresCacheExpiration;

    if (!this.featuresLastCachedData || isCacheExpired) {
      this.featuresLastCachedData = this.http
        .get<GenericItems<LorenFeature>>(this.endpoints.userFeatures())
        .pipe(
          shareReplay(1),
          map((x) => x.items)
        );
      this.featuresLastCalled = new Date();
    }

    return this.featuresLastCachedData;
  }

  private setSession(session: LorenLocalStorageSession) {
    window.localStorage.setItem(LorenSession.sessionKey, JSON.stringify(session));
  }
}
